import { FunctionComponent } from 'react'
import Button from '~/components/Button'
import { label, prefix } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'

type Props = {
  /**
   * The modal's own base label, without an action suffix — it appends `-cancel`, `-filtered-download`
   * and `-all-download` itself, which is why this is not a finished `TrackingLabel`.
   */
  trackingLabel: `${prefix}-${label}`
  onCancel: () => void
  onDownloadFiltered: () => void
  onDownloadAll: () => void
}

/**
 * The warning before a filtered mapping file leaves the app.
 *
 * The filter lives on the spreadsheet and the download lives in this flyout, so without a word here the
 * two quietly disagree: the file would arrive short of the ad units the publisher never asked to drop,
 * and a re-import of it would look like the mapping had been emptied. Both ways out are offered rather
 * than just the safe one — the filtered file is usually the point of having turned the filter on.
 *
 * `ConfirmationModal` cannot express this: its two buttons are Cancel and Confirm, with no room for a
 * second way forward.
 */
export const ConfirmFilteredMappingDownloadModal: FunctionComponent<Props> = ({ trackingLabel, onCancel, onDownloadFiltered, onDownloadAll }) => (
  <Modal.Content>
    <Modal.Title>Download filtered mapping</Modal.Title>
    <Modal.Body>
      <div className={'flex flex-col gap-3'}>
        <span>
          <strong>Only ad units used in a stack</strong> is on, so the file will contain only the ad units your medias' stacks deploy. The ad units in your inventory that no stack uses will be left
          out.
        </span>
        <span>Server-side ad units are always included — they are not tied to a media's stacks.</span>
      </div>
    </Modal.Body>
    <Modal.Actions>
      <Button variant={'tertiary'} onClick={onCancel} trackingLabel={`${trackingLabel}-cancel`}>
        Cancel
      </Button>
      <Button variant={'secondary'} onClick={onDownloadAll} trackingLabel={`${trackingLabel}-all-download`}>
        Download all
      </Button>
      <Button onClick={onDownloadFiltered} trackingLabel={`${trackingLabel}-filtered-download`}>
        Download filtered
      </Button>
    </Modal.Actions>
  </Modal.Content>
)
